import React from "react";
import { css } from "@emotion/react";
import { gotham } from "@/utils/fonts";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useEffect, useState } from "react";
import InfoIcon from "@/svg/InfoIcon";
import JWTooltip from "@/components/JWTooltip/JWTooltip";

interface FeatureTableProps {
  featureTableData: {
    recommendationText: string;
    tier1Label: string;
    tier2Label: string;
    tier3Label: string;
    tier1Icon: any;
    tier2Icon: any;
    tier3Icon: any;
    communicationLabelTable: string;
    communicationLabelList: string;
    communicationQuotasTable: string[];
    communicationQuotasList: string[];
    featureRows: Array<{
      fields: {
        label: string;
        values: boolean[];
        tooltipText?: string;
        comingSoon?: boolean[];
        tier1Availability: boolean;
        tier2Availability: boolean;
        tier3Availability: boolean;
        tier1ComingSoon: boolean;
        tier2ComingSoon: boolean;
        tier3ComingSoon: boolean;
      };
    }>;
  };
}

interface AthleteTier {
  text: string;
  icon: any;
}

interface CommFeature {
  label: string;
  values: string[];
}

interface Feature {
  label: string;
  values: boolean[];
  tooltip?: string;
  comingSoon?: boolean[];
}

const CheckIcon: React.FC = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="12" fill="#D2FFDE" />
    <path
      d="M7 12L10.75 15L17 9"
      stroke="#55D278"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossIcon: React.FC = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="12" fill="#EFF1F8" />
    <path
      d="M16 8L12 12M12 12L8 8M12 12L8 16M12 12L16 16"
      stroke="#657197"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FeatureTableDesktop: React.FC<{
  recommendationText: string;
  athleteTiers: AthleteTier[];
  comms: CommFeature[];
  features: Feature[];
}> = ({ recommendationText, athleteTiers, comms, features }) => {
  return (
    <table
      className={gotham.className}
      css={css`
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        background: #fff;
        border-collapse: separate;
        border-spacing: 0;
        overflow: hidden;
        box-shadow: none;
        table-layout: fixed;
      `}
    >
      <thead>
        <tr>
          <th
            css={css`
              background: transparent;
              color: #243b4a;
              font-size: 15px;
              font-weight: 500;
              text-align: left;
              padding: 24px 24px 24px 24px;
              border: none;
              box-sizing: border-box;
              width: 280px;
              min-width: 280px;
              max-width: 280px;
            `}
          >
            {recommendationText}
          </th>
          {athleteTiers.map((tier, idx) => (
            <th
              key={tier.text}
              css={css`
                background: #f8fcff;
                color: #048cdd;
                font-size: 14px;
                font-weight: 500;
                text-align: center;
                padding: 24px 0 24px 0;
                border-top: 2px solid #daf2ff;
                border-left: ${idx === 0 ? "2px solid #daf2ff" : "none"};
                border-right: ${idx === athleteTiers.length - 1
                  ? "2px solid #daf2ff"
                  : idx < athleteTiers.length - 1
                  ? "2px solid #daf2ff"
                  : "none"};
                min-width: 180px;
                ${idx === 0 ? "border-radius: 24px 0 0 0;" : ""}
                ${idx === athleteTiers.length - 1
                  ? "border-radius: 0 24px 0 0;"
                  : ""}
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <span
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 8px;
                  `}
                >
                  {tier.icon && (
                    <img
                      src={tier.icon.fields.file.url}
                      alt=""
                      style={{ width: 24, height: "auto", display: "block" }}
                    />
                  )}
                </span>
                <span
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    height: 24px;
                  `}
                >
                  {tier.text}
                </span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {comms.map(feature => (
          <tr key={feature.label}>
            <td
              css={css`
                color: var(--Main-Dark, #243b4a);
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: normal;
                padding: 0 24px;
                border: none !important;
                box-sizing: border-box;
                width: 280px;
                min-width: 280px;
                max-width: 280px;
                background: transparent;
                vertical-align: middle;
              `}
            >
              <div>
                <span
                  css={css`
                    margin-right: "0px";
                  `}
                >
                  {feature.label}
                </span>
              </div>
            </td>
            {feature.values.map((val, colIdx) => {
              const isFirstCol = colIdx === 0;
              return (
                <td
                  key={colIdx}
                  css={css`
                    padding-left: 20px;
                    padding-right: 20px;
                    width: 72px;
                    height: 72px;
                    min-width: 72px;
                    min-height: 72px;
                    max-width: 72px;
                    max-height: 72px;
                    text-align: center;
                    vertical-align: middle;
                    background: #fff;
                    border-left: ${isFirstCol ? "2px solid #daf2ff" : "none"};
                    border-right: 2px solid #daf2ff;
                    border-top: 2px solid #daf2ff;
                  `}
                >
                  {val}
                </td>
              );
            })}
          </tr>
        ))}
        {features.map((feature, rowIdx) => (
          <tr key={feature.label}>
            <td
              css={css`
                color: var(--Main-Dark, #243b4a);
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: normal;
                padding: 0 24px;
                border: none !important;
                box-sizing: border-box;
                width: 280px;
                min-width: 280px;
                max-width: 280px;
                background: transparent;
                vertical-align: middle;
              `}
            >
              <div css={css``}>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    margin-bottom: 6px;
                  `}
                >
                  {feature.label}{" "}
                  {(() => {
                    return (
                      feature.tooltip && (
                        <span
                          css={css`
                            margin-left: 6px;
                            cursor: pointer;
                            position: relative;
                            display: inline-flex;
                            align-items: center;
                          `}
                        >
                          <JWTooltip
                            title={feature.tooltip}
                            placement="top"
                            maxWidth={200}
                            className={gotham.className}
                          >
                            <InfoIcon style={{ width: 16, height: 16 }} />
                          </JWTooltip>
                        </span>
                      )
                    );
                  })()}
                </div>
                {feature.comingSoon && (
                  <span
                    css={css`
                      display: inline;
                      background: #f5f5f5;
                      color: #657197;
                      font-size: 11px;
                      border-radius: 40px;
                      padding: 2px 8px;
                      font-weight: 500;
                      white-space: nowrap;
                    `}
                  >
                    Coming soon!
                  </span>
                )}
              </div>
            </td>
            {feature.values &&
              feature.values.map((val, colIdx) => {
                // Border radius for first value column (second column overall)
                let borderRadius = "";
                if (colIdx === 0 && rowIdx === features.length - 1)
                  borderRadius = "0 0 0 24px";
                if (
                  colIdx === feature.values.length - 1 &&
                  rowIdx === features.length - 1
                ) {
                  borderRadius = "0 0 24px 0";
                }
                // Outer border effect
                const isTopRow = rowIdx === 0;
                const isBottomRow = rowIdx === features.length - 1;
                const isLastCol = colIdx === feature.values.length - 1;
                return (
                  <td
                    key={colIdx}
                    css={css`
                      padding-left: 20px;
                      padding-right: 20px;
                      width: 72px;
                      height: 72px;
                      min-width: 72px;
                      min-height: 72px;
                      max-width: 72px;
                      max-height: 72px;
                      text-align: center;
                      vertical-align: middle;
                      background: #fff;
                      border-left: ${colIdx === 0
                        ? "2px solid #daf2ff"
                        : "none"};
                      border-right: ${isLastCol
                        ? "2px solid #daf2ff"
                        : colIdx < feature.values.length - 1
                        ? "2px solid #daf2ff"
                        : "none"};
                      border-top: ${isTopRow ? "2px solid #daf2ff" : "none"};
                      border-bottom: ${isBottomRow
                        ? "2px solid #daf2ff"
                        : "2px solid #daf2ff"};
                      ${borderRadius ? `border-radius: ${borderRadius};` : ""}
                    `}
                  >
                    {typeof val === "boolean" ? (
                      val ? (
                        <CheckIcon />
                      ) : (
                        <CrossIcon />
                      )
                    ) : (
                      val
                    )}
                  </td>
                );
              })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const FeatureTableMobile: React.FC<{
  athleteTiers: AthleteTier[];
  comms: CommFeature[];
  features: Feature[];
}> = ({ athleteTiers, comms, features }) => {
  return (
    <div
      className={gotham.className}
      css={css`
        display: flex;
        flex-direction: column;
        width: 100%;
        margin: 0 auto;
        box-sizing: border-box;
        border-radius: 15px;
        border: 2px solid #daf2ff;
        // max-width: 400px;
        overflow: hidden;
      `}
    >
      {/* Header */}
      <div
        css={css`
          text-align: center;
          font-size: 15px;
          font-weight: 500;
          color: #243b4a;
          padding: 20px;
          padding-left: 5px;
          padding-right: 5px;
          background: #f5fbff;
          overflow: hidden;
          border-bottom: 2px solid #daf2ff;
        `}
      >
        <div
          css={css`
            color: var(--Main-Dark, #243b4a);
            text-align: center;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 136%; /* 16.32px */
          `}
        >
          Recommended for Organizations with
        </div>
        <div
          css={css`
            margin-top: 20px;
            padding-left: 20px;
            padding-right: 20px;
            display: flex;
            flex-direction: row;
            justify-content: space-around;
            align-items: center;
          `}
        >
          {athleteTiers.map(tier => (
            <div
              key={tier.text}
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;

                color: var(--Main-Light-Blue, #048cdd);
                text-align: center;
                font-size: 12px;
                font-style: normal;
                font-weight: 350;
                line-height: normal;
              `}
            >
              {tier.icon && (
                <img
                  src={tier.icon.fields.file.url}
                  alt=""
                  style={{
                    width: 24,
                    height: 24,
                    display: "block",
                    strokeWidth: 4,
                  }}
                />
              )}
              <span
                css={css`
                  max-width: 80px;
                `}
              >
                {tier.text}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Com Limits */}
      {comms.map(feature => (
        <div
          key={feature.label}
          css={css`
            display: flex;
            flex-direction: column;
            padding: 20px;
            border-bottom: 2px solid #daf2ff;
          `}
        >
          {/* Feature label */}
          <div
            css={css`
              display: flex;
              justify-content: space-around;
              align-items: center;
              background: none;

              color: var(--Main-Dark, #243b4a);
              text-align: center;
              font-size: 12px;
              font-style: normal;
              font-weight: 400;
              line-height: 136%; /* 16.32px */
            `}
          >
            <span>{feature.label}</span>
          </div>
          {/* Feature values row */}
          <div
            css={css`
              margin-top: 20px;
              display: flex;
              flex-direction: row;
              justify-content: space-around;
              align-items: center;
            `}
          >
            {feature.values &&
              feature.values.map((val, idx) => (
                <div
                  key={idx}
                  css={css`
                    min-width: 80px;
                    color: var(--Main-Dark, #243b4a);
                    text-align: center;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 350;
                    line-height: normal;
                    letter-spacing: -0.12px;
                  `}
                >
                  {val}
                </div>
              ))}
          </div>
        </div>
      ))}

      {/* Features */}
      {features &&
        features.map((feature, featureIdx) => (
          <div
            key={feature.label}
            css={css`
              display: flex;
              flex-direction: column;
              padding: 20px;
              border-bottom: ${featureIdx === features.length - 1
                ? "none"
                : "2px solid #daf2ff"};
            `}
          >
            {/* Feature label */}
            <div
              css={css`
                display: flex;
                justify-content: space-around;
                align-items: center;
                background: none;

                color: var(--Main-Dark, #243b4a);
                text-align: center;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 136%; /* 16.32px */
              `}
            >
              <div
                css={css`
                  display: flex;
                  gap: 3px;
                  align-items: center;
                `}
              >
                <span>{feature.label}</span>
                {(() => {
                  return (
                    feature.tooltip && (
                      <span
                        css={css`
                          margin-left: 6px;
                          cursor: pointer;
                          position: relative;
                          display: inline-flex;
                          align-items: center;
                        `}
                      >
                        <JWTooltip
                          title={feature.tooltip}
                          placement="top"
                          maxWidth={200}
                          className={gotham.className}
                        >
                          <InfoIcon style={{ width: 16, height: 16 }} />
                        </JWTooltip>
                      </span>
                    )
                  );
                })()}
                {feature.comingSoon && (
                  <span
                    css={css`
                      background: var(--Dividers, #eff1f8);
                      color: #657197;
                      border-radius: 40px;
                      padding: 2px 8px;
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      flex-shrink: 0;
                      font-size: 8px;
                      font-style: normal;
                      font-weight: 350;
                      line-height: normal;
                    `}
                  >
                    Coming soon!
                  </span>
                )}
              </div>
            </div>
            {/* Feature values row */}
            <div
              css={css`
                width: 100%;
                margin-top: ${feature.tooltip ? "16px" : "20px"};
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
              `}
            >
              {feature.values &&
                feature.values.map((val, idx) => (
                  <div
                    key={idx}
                    css={css`
                      flex: 1;
                      text-align: center;
                      min-width: 80px;
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 2px;
                    `}
                  >
                    {typeof val === "boolean" ? (
                      val ? (
                        <CheckIcon />
                      ) : (
                        <CrossIcon />
                      )
                    ) : (
                      <span>{val}</span>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export const FeatureTable: React.FC<FeatureTableProps> = ({
  featureTableData,
}) => {
  const [mounted, setMounted] = useState(false);
  const { isLg, isXl, isXl2, isXl3 } = useWindowSize();
  const isDesktop = isLg || isXl || isXl2 || isXl3;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return (
      <div className={gotham.className}>
        <div>Loading...</div>
      </div>
    );
  }

  const athleteTiers: AthleteTier[] = [
    {
      text: featureTableData.tier1Label,
      icon: featureTableData.tier1Icon,
    },
    {
      text: featureTableData.tier2Label,
      icon: featureTableData.tier2Icon,
    },
    {
      text: featureTableData.tier3Label,
      icon: featureTableData.tier3Icon,
    },
  ];

  const tableComms: CommFeature[] = [
    {
      label: featureTableData.communicationLabelTable,
      values: featureTableData.communicationQuotasTable,
    },
  ];

  const listComms: CommFeature[] = [
    {
      label: featureTableData.communicationLabelList,
      values: featureTableData.communicationQuotasList,
    },
  ];

  const features: Feature[] = featureTableData.featureRows.map(row => {
    const feature = {
      label: row.fields.label,
      values: [
        row.fields.tier1Availability,
        row.fields.tier2Availability,
        row.fields.tier3Availability,
      ],
      tooltip: row.fields.tooltipText,
      comingSoon: row.fields.comingSoon,
    };

    return feature;
  });

  return (
    <div className={gotham.className}>
      {isDesktop ? (
        <FeatureTableDesktop
          recommendationText={featureTableData.recommendationText}
          athleteTiers={athleteTiers}
          comms={tableComms}
          features={features}
        />
      ) : (
        <FeatureTableMobile
          athleteTiers={athleteTiers}
          comms={listComms}
          features={features}
        />
      )}
    </div>
  );
};

export default FeatureTable;
